import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Swords, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPlan } from "@/lib/plans";
import UpgradeLock from "@/components/UpgradeLock";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

export default function Intel() {
  const { user } = useAuth();
  const locked = !hasPlan(user?.plan, "pro");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (locked) return;
    api.get("/competitive-intel").then((r) => setData(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [locked]);

  if (locked) return <UpgradeLock required="pro" feature="Competitive Intelligence" description="Upgrade to Pro to see top incumbents and issuing-organization spending in your market." />;
  if (loading) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  const maxComp = Math.max(...(data?.top_competitors || []).map((c) => c.total_awarded), 1);

  return (
    <div className="space-y-6" data-testid="intel-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Competitive Intelligence</h1>
        <p className="text-slate-500 mt-1">Who's winning in your trade — powered by live USAspending award data.</p>
      </div>

      {(!data || data.source === "unavailable" || data.top_competitors.length === 0) ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center text-slate-400" data-testid="intel-empty">
          Set your trade in Profile Builder to see incumbents and issuing-organization spending in your market.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><Swords className="h-5 w-5 text-slate-900" /> Top Incumbent Contractors</h3>
            <div className="space-y-4">
              {data.top_competitors.map((c, i) => (
                <div key={c.name || i} data-testid={`competitor-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium truncate pr-2">{c.name}</span>
                    <span className="font-data text-slate-500 shrink-0">{fmt(c.total_awarded)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-900" style={{ width: `${(c.total_awarded / maxComp) * 100}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{c.award_count} awards</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><Building2 className="h-5 w-5 text-slate-900" /> Top Spending Organizations</h3>
            <div className="space-y-3">
              {data.top_agencies.map((a, i) => (
                <div key={a.agency || i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`agency-${i}`}>
                  <span className="text-sm text-slate-700 truncate pr-2">{a.agency}</span>
                  <span className="font-data text-sm font-medium text-emerald-600 shrink-0">{fmt(a.total)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
